import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, MessageSquare, Pin, PinOff, Send, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

// Poll every 12s while the panel is open — cheap enough for a per-board
// chat and avoids the complexity of adding a websocket layer just for this.
const POLL_MS = 12_000;

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const same = d.toDateString() === now.toDateString();
  return same
    ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const Avatar = ({ picture, name, size = 24 }) => {
  const initial = ((name || "?").trim()[0] || "?").toUpperCase();
  return picture ? (
    <img src={picture} alt="" className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />
  ) : (
    <div
      className="rounded-full bg-slate-300 text-slate-700 flex items-center justify-center font-semibold shrink-0 text-[10px]"
      style={{ width: size, height: size }}
    >
      {initial}
    </div>
  );
};

const Message = ({ m, mine, canModerate, onPin, onDelete }) => (
  <div className={`group flex gap-2 ${mine ? "flex-row-reverse text-right" : ""}`} data-testid={`chat-msg-${m.id}`}>
    <Avatar picture={m.user_picture} name={m.user_name} />
    <div className={`min-w-0 flex-1 ${mine ? "items-end" : ""}`}>
      <div className={`flex items-center gap-1.5 ${mine ? "justify-end" : ""}`}>
        <span className="text-[10px] font-semibold text-slate-700 truncate">{mine ? "Tu" : m.user_name}</span>
        <span className="text-[9px] text-slate-400">{fmtTime(m.created_at)}</span>
        {m.pinned && (
          <span className="inline-flex items-center gap-0.5 text-[8px] uppercase font-semibold text-amber-700 bg-amber-100 px-1 rounded" title="Messaggio appuntato">
            <Pin className="w-2.5 h-2.5" /> Fisso
          </span>
        )}
      </div>
      <div
        className={`text-[12px] leading-snug rounded-2xl px-3 py-1.5 mt-1 inline-block break-words whitespace-pre-wrap ${
          mine ? "bg-[#0A84FF] text-white rounded-tr-sm" : "bg-slate-100 text-slate-800 rounded-tl-sm"
        }`}
        style={{ maxWidth: "92%" }}
      >
        {m.text}
      </div>
      {(mine || canModerate) && (
        <div className={`opacity-0 group-hover:opacity-100 transition-opacity mt-0.5 flex gap-1 text-[10px] ${mine ? "justify-end" : ""}`}>
          {canModerate && (
            <button
              className="text-slate-500 hover:text-amber-700 inline-flex items-center gap-0.5"
              onClick={() => onPin(m)}
              data-testid={`chat-pin-${m.id}`}
            >
              {m.pinned ? <><PinOff className="w-3 h-3" /> Rimuovi</> : <><Pin className="w-3 h-3" /> Appunta</>}
            </button>
          )}
          {(mine || canModerate) && (
            <button
              className="text-slate-500 hover:text-rose-600 inline-flex items-center gap-0.5"
              onClick={() => onDelete(m)}
              data-testid={`chat-del-${m.id}`}
            >
              <Trash2 className="w-3 h-3" /> Elimina
            </button>
          )}
        </div>
      )}
    </div>
  </div>
);

const BoardChat = ({ boardId, isOwner = false, collapsed, onToggle }) => {
  const { user } = useAuth();
  const [pinned, setPinned] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const canModerate = isOwner || user?.role === "admin";

  const load = useCallback(async () => {
    if (!boardId) return;
    try {
      const r = await api.get(`/boards/${boardId}/chat`);
      setPinned(r.data?.pinned || []);
      setMessages(r.data?.messages || []);
    } catch {
      // silent — sidebar shouldn't scream if the network hiccups
    }
  }, [boardId]);

  useEffect(() => {
    if (collapsed) return;
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, [collapsed, load]);

  // Autoscroll to bottom on new messages (only when the list is already
  // near the bottom, so we don't yank the user out of scrolled-up reading).
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/boards/${boardId}/chat`, { text: t });
      setMessages((prev) => [...prev, r.data]);
      setText("");
      setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 30);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio messaggio");
    } finally {
      setSending(false);
    }
  };

  const onPin = async (m) => {
    try {
      if (m.pinned) {
        await api.delete(`/boards/${boardId}/chat/${m.id}/pin`);
      } else {
        await api.post(`/boards/${boardId}/chat/${m.id}/pin`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const onDelete = async (m) => {
    try {
      await api.delete(`/boards/${boardId}/chat/${m.id}`);
      setPinned((prev) => prev.filter((x) => x.id !== m.id));
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const totalCount = pinned.length + messages.length;

  return (
    <div className="ios-card overflow-hidden" data-testid="board-chat-card">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors"
        onClick={onToggle}
        data-testid="board-chat-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-[#0A84FF] to-[#12466F] flex items-center justify-center text-white shrink-0">
            <MessageSquare className="w-3.5 h-3.5" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold leading-none">Chat scheda</div>
            <div className="font-display font-bold text-sm leading-tight text-slate-800 mt-0.5">
              Messaggi <span className="text-slate-400 font-mono-num font-normal">· {totalCount}</span>
            </div>
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${collapsed ? "-rotate-90" : ""}`}
          strokeWidth={2}
        />
      </button>

      {!collapsed && (
        <div className="px-3 pb-3">
          {/* Pinned block (never pruned, always at top). */}
          {pinned.length > 0 && (
            <div className="mb-2 bg-amber-50/60 border border-amber-200 rounded-xl p-2 space-y-2" data-testid="chat-pinned-list">
              <div className="text-[9px] uppercase tracking-[0.2em] font-semibold text-amber-800 flex items-center gap-1">
                <Pin className="w-3 h-3" /> Appuntati
              </div>
              {pinned.map((m) => (
                <Message
                  key={m.id}
                  m={m}
                  mine={m.user_id === user?.user_id}
                  canModerate={canModerate}
                  onPin={onPin}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}

          {/* Scrollable message log. Sized to ~3 messages of 2 lines. */}
          <div
            ref={listRef}
            className="overflow-y-auto pr-1 space-y-2"
            style={{ maxHeight: "200px", minHeight: totalCount === 0 ? "60px" : "80px" }}
            data-testid="chat-messages-list"
          >
            {messages.length === 0 && pinned.length === 0 && (
              <div className="text-[11px] text-slate-400 italic text-center py-3">
                Nessun messaggio. Rompi il ghiaccio!
              </div>
            )}
            {messages.map((m) => (
              <Message
                key={m.id}
                m={m}
                mine={m.user_id === user?.user_id}
                canModerate={canModerate}
                onPin={onPin}
                onDelete={onDelete}
              />
            ))}
          </div>

          {/* Composer */}
          <div className="mt-2 flex items-end gap-1.5">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder="Scrivi un messaggio…"
              rows={2}
              maxLength={2000}
              className="flex-1 min-w-0 text-[12px] rounded-xl border border-slate-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0A84FF]/40 focus:border-[#0A84FF] resize-none"
              data-testid="chat-input"
            />
            <Button
              size="sm"
              className="rounded-full h-8 w-8 p-0 bg-[#0A84FF] hover:bg-[#0060df] shrink-0"
              onClick={send}
              disabled={!text.trim() || sending}
              data-testid="chat-send-btn"
              title="Invia (Invio)"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardChat;
